import { ColDef, SetFilterValuesFuncParams } from 'ag-grid-enterprise';
import { TFunction } from 'i18next';

import tableStyles from '../../../styles/components/table.module.css';
import styles from '../styles.module.css';

/**
 * Reusable collator instance for natural sorting
 * Creating this once and reusing improves performance significantly
 */
const naturalSortCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: 'base',
});

/**
 * Natural sort comparator for strings containing numbers
 * Handles cases like "Sample 1", "Sample 2", "Sample 10" correctly
 * - Null/undefined values are sorted first
 * - Empty strings are treated as valid values and sorted naturally
 * - Uses Intl.Collator for locale-aware, numeric string comparison
 */
function naturalSortComparator(valueA: string | null | undefined, valueB: string | null | undefined): number {
  // Handle null/undefined values
  if ((valueA === null || valueA === undefined) && (valueB === null || valueB === undefined)) return 0;
  if (valueA === null || valueA === undefined) return -1;
  if (valueB === null || valueB === undefined) return 1;

  return naturalSortCollator.compare(valueA, valueB);
}

/**
 * Advanced text filter parameters for AG Grid columns
 * Enables multiple filter options with proper UI
 */
const TEXT_FILTER_PARAMS = {
  filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith', 'blank', 'notBlank'],
  maxNumConditions: 2,
  buttons: ['reset', 'apply'],
  defaultOption: 'contains',
  caseSensitive: false,
  debounceMs: 500,
  trimInput: true,
};

/**
 * Advanced number filter parameters for AG Grid columns
 * Supports range filtering and multiple conditions
 */
const NUMBER_FILTER_PARAMS = {
  filterOptions: [
    'equals',
    'notEqual',
    'lessThan',
    'greaterThan',
    'lessThanOrEqual',
    'greaterThanOrEqual',
    'inRange',
    'blank',
    'notBlank',
  ],
  maxNumConditions: 2,
  buttons: ['reset', 'apply'],
  defaultOption: 'equals',
  debounceMs: 500,
  allowedCharPattern: '[0-9.-]',
};

/**
 * Advanced date filter parameters for AG Grid columns.
 * Omits 'lessThanOrEqual'/'greaterThanOrEqual' — the API only has exclusive 'before'/'after' operators.
 */
const DATE_FILTER_PARAMS = {
  filterOptions: ['equals', 'notEqual', 'lessThan', 'greaterThan', 'inRange', 'blank', 'notBlank'],
  maxNumConditions: 2,
  buttons: ['reset', 'apply'],
  defaultOption: 'equals',
  debounceMs: 500,
  comparator: (filterDate: Date, cellValue: string | Date) => {
    const cellDate = new Date(cellValue);
    if (cellDate < filterDate) return -1;
    if (cellDate > filterDate) return 1;
    return 0;
  },
};

/**
 * Set filter parameters for categorical data with filter lists
 * Uses checkboxes to select from a list of distinct values
 *
 * For server-side row models, values are extracted from the cached data in AG Grid
 * using the `values` callback function which receives the filter params with access
 * to the grid API.
 *
 * Features:
 * - Automatically extracts distinct values from cached row data
 * - Case-insensitive matching for better UX
 * - Tooltips for truncated values
 * - Reset/apply buttons for filter control
 * - Works with both text and numeric values
 * - Accessible via column header menu (no floating filter row)
 */
const SET_FILTER_PARAMS = {
  buttons: ['reset', 'apply'],
  closeOnApply: false,
  caseSensitive: false,
  showTooltips: true,
  // Values are extracted from loaded data via callback in each column definition
};

/**
 * Creates Set Filter parameters that extract distinct values from AG Grid's cached data
 * Works with server-side row models by scanning the cache instead of making additional API calls
 *
 * @param field - The field name to extract distinct values from
 * @returns Complete filter params object for agSetColumnFilter
 */
export function createSetFilterParams(field: string): Record<string, unknown>;

/**
 * Creates Set Filter parameters with static values
 *
 * @param field - The field name (used for API filtering)
 * @param staticValues - Array of static values to show in the filter
 * @returns Complete filter params object for agSetColumnFilter
 */
export function createSetFilterParams(field: string, staticValues: string[]): Record<string, unknown>;

/**
 * Implementation
 */
export function createSetFilterParams(field: string, staticValues?: string[]): Record<string, unknown> {
  if (staticValues) {
    // Use static values provided
    return {
      ...SET_FILTER_PARAMS,
      values: staticValues,
    };
  }

  // Extract values from AG Grid cache
  return {
    ...SET_FILTER_PARAMS,
    values: (params: SetFilterValuesFuncParams) => {
      // Extract distinct values from AG Grid's row data cache
      const distinctValues = new Set<string>();

      // Iterate through the cache to get all loaded values
      params.api.forEachNode(node => {
        const value = node.data?.[field];
        if (value !== null) {
          distinctValues.add(String(value));
        }
      });

      // Convert Set to sorted array
      const valuesArray = Array.from(distinctValues).toSorted((a, b) => {
        // Natural sort for better UX (handles numbers in strings)
        return naturalSortCollator.compare(a, b);
      });

      // Call success callback with the values
      params.success(valuesArray);
    },
  };
}

/**
 * Base interface for sample records with common fields
 */
interface SampleRecordBase {
  sample_num: number;
  sample_name: string;
  instrument_run_name: string;
  well_position: string;
  demux_index: number;
}

/**
 * Factory function to create shared column definitions for sample-based tables
 * Provides consistent column configuration with Set Filters that extract values from cached data
 *
 * Uses Set Filters for categorical columns (sample_name, well_position, instrument_run_name, demux_index)
 * which automatically extract distinct values from AG Grid's cached row data.
 * This works seamlessly with server-side row models without additional API calls.
 *
 * All filters are accessible via the column header menu - no floating filter row.
 *
 * @param t - i18next translation function for 'studies' namespace
 * @returns Array of column definitions for sample-based grids
 */
export function createSharedSampleColumns<T extends SampleRecordBase>(t: TFunction<'studies'>): ColDef<T>[] {
  return [
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'sample_num' as keyof T,
      headerName: t('columnHeaders.sampleNum'),
      sortable: false, // Not supported by API
      filter: false,
      resizable: true,
    },
    {
      cellClass: styles.tableCell,
      comparator: naturalSortComparator,
      field: 'sample_name' as keyof T,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'notContains', 'equals', 'notEqual', 'startsWith', 'endsWith', 'blank', 'notBlank'],
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
      headerName: t('columnHeaders.sampleName'),
      resizable: true,
      sortable: true,
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'instrument_run_name' as keyof T,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
      headerName: t('columnHeaders.instrumentRun'),
      resizable: true,
      sortable: true,
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'well_position' as keyof T,
      headerName: t('columnHeaders.wellPosition'),
      sortable: true,
      filter: 'agTextColumnFilter',
      filterParams: {
        filterOptions: ['contains', 'equals', 'startsWith', 'blank', 'notBlank'],
        buttons: ['reset', 'apply'],
        defaultOption: 'contains',
        debounceMs: 500,
      },
      resizable: true,
    },
    {
      cellClass: tableStyles.tableCellCenter,
      field: 'demux_index' as keyof T,
      filter: 'agNumberColumnFilter',
      filterParams: {
        filterOptions: [
          'equals',
          'notEqual',
          'lessThan',
          'greaterThan',
          'lessThanOrEqual',
          'greaterThanOrEqual',
          'inRange',
          'blank',
          'notBlank',
        ],
        buttons: ['reset', 'apply'],
        defaultOption: 'equals',
        debounceMs: 500,
      },
      headerName: t('columnHeaders.demuxIndex'),
      resizable: true,
      sortable: true,
    },
  ] as ColDef<T>[];
}

/**
 * Export filter params for reuse in custom columns
 * These provide consistent filtering behavior across all table instances
 */
export { TEXT_FILTER_PARAMS, NUMBER_FILTER_PARAMS, DATE_FILTER_PARAMS, SET_FILTER_PARAMS };
